import React, { useEffect, useState } from 'react';
import { AlertCircle, BarChart2, Clock, ScrollText, Gift, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, KpiCard, cardStyle, sectionTitleStyle, thStyle, tdStyle } from '../shared';

export default function EncuestaAdminTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/survey/stats`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(()        => { if (!cancelled) setError('No se han podido cargar las estadísticas.'); })
      .finally(()      => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ padding: 24, color: 'rgba(224,247,255,0.55)' }}>Cargando estadísticas…</div>;
  if (error)   return <div className="register-error"><AlertCircle size={15} /><span>{error}</span></div>;
  if (!data || data.total === 0) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(224,247,255,0.65)' }}>
          <ClipboardList size={18} style={{ color: '#22d3ee' }} />
          <span>Aún no hay respuestas. Las estadísticas aparecerán cuando los jugadores envíen la encuesta.</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <KpiCard icon={<ClipboardList size={18} />} label="Respuestas totales" value={data.total.toLocaleString()} />
        <KpiCard icon={<Gift size={18} />}          label="Recompensa entregada" value={data.reward_granted.toLocaleString()}
                 subtitle={`${data.total ? Math.round(data.reward_granted * 100 / data.total) : 0}%`} />
        <KpiCard icon={<Clock size={18} />}         label="Recompensa pendiente" value={data.reward_pending.toLocaleString()}
                 subtitle="esperan login al juego" />
      </div>

      {/* Preguntas */}
      {data.questions.map((q) => (
        <QuestionBlock key={q.field} q={q} total={data.total} />
      ))}

      {/* Últimas respuestas */}
      <div style={cardStyle}>
        <h3 style={{ ...sectionTitleStyle, marginTop: 0 }}>
          <ScrollText size={16} /> Últimas 20 respuestas
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'rgba(224,247,255,0.55)', textAlign: 'left' }}>
                <th style={thStyle}>Fecha</th>
                <th style={thStyle}>Usuario</th>
                <th style={thStyle}>Cómo nos encontró</th>
                <th style={thStyle}>Horas/sem</th>
                <th style={thStyle}>Contenido</th>
                <th style={thStyle}>Atracción</th>
                <th style={thStyle}>Cristales</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={tdStyle}>{formatDate(r.submitted_at)}</td>
                  <td style={{ ...tdStyle, color: '#22d3ee', fontWeight: 600 }}>{r.username}</td>
                  <td style={tdStyle}>{formatAnswer(r.q1_source, 'q1_source')}</td>
                  <td style={tdStyle}>{formatAnswer(r.q2_hours, 'q2_hours')}</td>
                  <td style={tdStyle}>{formatAnswer(r.q3_content, 'q3_content')}</td>
                  <td style={tdStyle}>{formatAnswer(r.q4_appeal,  'q4_appeal')}</td>
                  <td style={tdStyle}>
                    {r.reward_granted
                      ? <span style={{ color: '#34d399' }}>✓ entregado</span>
                      : <span style={{ color: '#fbbf24' }}>pendiente</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function QuestionBlock({ q, total }) {
  const maxCount = Math.max(1, ...q.items.map((it) => it.count));
  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>
        <BarChart2 size={16} /> {q.title}
        <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(224,247,255,0.4)', marginLeft: 8 }}>
          {q.multi ? '(opción múltiple)' : '(opción única)'}
        </span>
      </h3>
      <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
        {q.items.map((it) => (
          <div key={it.value} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e0f7ff', marginBottom: 4 }}>
                <span>{it.label}</span>
                <span style={{ color: 'rgba(224,247,255,0.55)', fontSize: 12 }}>
                  {it.count} · {it.percent}%
                </span>
              </div>
              <div style={{ height: 8, background: 'rgba(34,211,238,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${(it.count / maxCount) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0ea5e9, #22d3ee)',
                  transition: 'width .3s ease',
                }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(224,247,255,0.4)' }}>
        {q.multi
          ? `Sobre ${total} respuestas (cada jugador puede marcar varias opciones).`
          : `${q.answered} de ${total} respondieron.`}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Etiquetas para la tabla de "últimas respuestas". Coinciden con backend.
const _RECENT_LABELS = {
  q1_source: {
    youtube_twitch: 'YouTube/Twitch', reddit_forums: 'Reddit/foros', discord: 'Discord',
    tiktok_instagram: 'TikTok/IG', search_engine: 'Buscador', top_lists: 'Top lists',
    word_of_mouth: 'Boca a boca',
  },
  q2_hours: { lt_5: '<5h', '5_15': '5-15h', '15_30': '15-30h', gt_30: '>30h' },
  q3_content: {
    pve_raids: 'Raids', dungeons_mythic: 'Mazmorras', pvp: 'PvP', leveling: 'Leveling',
    professions_economy: 'Profesiones', roleplay_social: 'RP/social',
    achievements: 'Achievements', playerbots: 'Playerbots',
  },
  q4_appeal: {
    playerbots: 'Playerbots', community: 'Comunidad', rates_progression: 'Rates',
    custom_content: 'Custom', stability: 'Estabilidad', free_no_p2w: 'Gratis/no P2W',
    friend_streamer: 'Amigo/streamer', quality_of_life: 'QoL',
  },
};

function formatAnswer(value, field) {
  if (value == null) return '—';
  const dict = _RECENT_LABELS[field] || {};
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((v) => dict[v] || v).join(', ');
  }
  return dict[value] || value;
}
